import {Injectable} from '@angular/core';
import {ModalController, ToastController} from '@ionic/angular';
import {Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {UnrApiService} from '../core/api/api.service';
import {CommentPayload} from '../core/api/payloads/comment.payload';
import {Comment} from '../core/api/resources/comment.resource';
import {Roadtrip} from '../core/api/resources/roadtrip-list.resource';
import {Step} from '../core/api/resources/step.resource';
import {UnrAuthService} from '../core/auth/auth.service';
import {UnrAddCommentComponent} from '../feedback/components/add-comment/add-comment.component';
import {AddTimelineComponent} from '../feedback/components/add-timeline/add-timeline.component';
import {AddTimelinePage} from '../pages/add-timeline/add-timeline.page';
import {AngularFireAuth} from '@angular/fire/compat/auth';

@Injectable(
  {providedIn: 'root'}
)
export class UnrFeedbackService {

  private modalAddComment: HTMLIonModalElement;
  private modalTimeline: HTMLIonModalElement;
  private toastFeedbackProvided: HTMLIonToastElement;

  constructor(private apiService: UnrApiService,
              private angularFireAuth: AngularFireAuth,
              private authService: UnrAuthService,
              private modalController: ModalController,
              private toastController: ToastController) {
  }

  getFeedbackForTrip(tripId: number): Observable<Comment[]> {
    return this.apiService
      .getCommentsForRoadTrip(`${tripId}`)
      .pipe(
        map(response => response.comments),
        map((comments: Comment[]) => {
          comments.forEach(this.setUserNameForAnonymousAppleIdUser);
          return comments;
        })
      );
  }

  getFeedbackForStep(stepId: number): Observable<Comment[]> {
    return this.apiService
      .getCommentsForStep((`${stepId}`))
      .pipe(
        map(response => response.comments),
        map((comments: Comment[]) => {
          comments.forEach(this.setUserNameForAnonymousAppleIdUser);
          return comments;
        })
      );
  }

  async showAddCommentAndRatingModalForStep(hasCommentedForTrip: boolean = false): Promise<HTMLIonModalElement> {
    this.modalAddComment = await this.modalController.create({
      component: UnrAddCommentComponent,
      componentProps: {
        isStep: true,
        hasCommentedForTrip
      },
      mode: 'md',
      backdropDismiss: true,
      cssClass: 'modal--popup'
    });

    await this.modalAddComment.present();

    return this.modalAddComment;
  }

  async showAddCommentAndRatingModalForTrip(): Promise<HTMLIonModalElement> {
    this.modalAddComment = await this.modalController.create({
      component: UnrAddCommentComponent,
      componentProps: {
        isStep: false,
        skipCommentForTripRequest: true
      },
      cssClass: 'modal--popup',
      backdropDismiss: true,
      mode: 'md'
    });

    await this.modalAddComment.present();

    return this.modalAddComment;
  }
  async showTimelineModal(): Promise<HTMLIonModalElement> {
    this.modalTimeline = await this.modalController.create({
      component: AddTimelinePage,
      componentProps: {
        isStep: false,
        skipCommentForTripRequest: true
      },
      cssClass: 'modal--popup',
      backdropDismiss: true,
      mode: 'md'
    });

    await this.modalTimeline.present();

    return this.modalTimeline;
  }

  async postFeedbackForTrip(trip: Roadtrip, payload: CommentPayload): Promise<Observable<Comment[]>> {
    return this.apiService
      .postCommentAndRatingForRoadtrip(trip.id, {
        rating: payload.rating,
        comment: payload.comment,
        images: payload.images,
        firebaseUid: localStorage.getItem('uid')
      })
      .pipe(
        switchMap(() => this.getFeedbackForTrip(trip.id))
      );
  }

  async postFeedbackForStep(step: Step, payload: CommentPayload): Promise<Observable<Comment[]>> {
    return this.apiService
      .postCommentAndRatingForStep(step.id, {
        rating: payload.rating,
        comment: payload.comment,
        firebaseUid: localStorage.getItem('uid'),
        images: payload.images
      })
      .pipe(
        switchMap(() => this.getFeedbackForStep(step.id))
      );
  }

  getAverageRating(commentList: Comment[]): number {
    const sumRatings = commentList.reduce((previousValue: number, currentValue: Comment) => {
      previousValue += currentValue.rating;
      return previousValue;
    }, 0);
    return sumRatings / commentList.length;
  }

  hasUserAlreadyProvidedFeedback(feedbackList: Comment[]): boolean {

    return feedbackList.find( item => item.user.firebaseUid === localStorage.getItem('uid')) !== undefined;
  }

  async showToastFeedbackProvided(): Promise<HTMLIonToastElement> {
    if (this.toastFeedbackProvided) {
      await this.toastFeedbackProvided.dismiss();
    }

    this.toastFeedbackProvided = await this.toastController.create({
      message: 'You already provided a rating or comment.',
      duration: 3000
    });

    await this.toastFeedbackProvided.present();

    return this.toastFeedbackProvided;
  }

  private setUserNameForAnonymousAppleIdUser(comment: Comment): void {
    if (comment.user.email === null) {
      return;
    }
    if (comment.user.email.indexOf('appleid') > -1) {
      comment.user.name = 'Apple Id User';
    } else if (comment.user.name === null) {
      comment.user.name = comment.user.email.split('@')[0];
    }
  }
}
